/*
* Copyright  2025-2026, Airspan
* All Rights Reserved
*
* This code is unpublished, confidential and proprietary to Airspan and may only be used in accordance with the appropriate
* license agreement. All other copying, modification, distribution, or
* reverse engineering are strictly prohibited.
*/

import React, { useEffect, useState } from "react";
import { setLabelList, setSelectedNotifyLabel } from "../../../store/api/devices/reducers";
import { useDeleteLabelMutation, useGetLabelsQuery } from "../../../store/api/devices/deviceApiSlice";
import { useStoreDispatch } from "../../../store/hooks";
import { setOpenToast } from "../../../store/helpers/helperReducers";
import { errorMessage } from "../../../utils/helpers";
import Devices from "../../../components/Administrations/LabelManagements/Devices/Devices";
import { useSelector } from "react-redux";
import { labelListSelector } from "../../../store/api/devices/selectors";
import { setLabelName } from "../../../store/filters/devices/Label/LabelDeviceFilterReducer";
import { Ioptions } from "../../../components/Shared/Filter/types";


export const useLabelConfiguration = () => {
  const dispatch = useStoreDispatch();
  const labelsData = useGetLabelsQuery();
  const labelList = useSelector(labelListSelector) || [];
  const [selectedItem, setSelectedItem] = useState<Ioptions | undefined>(labelList[0]);
  const devicesComponent = React.createElement(Devices, {
    key: selectedItem?.value ?? "__no-label__",
    onAssociationChanged: labelsData.refetch,
  });

  useEffect(() => {
    const labelData = labelsData.data || [];
    if (labelData.length > 0) {
      const sortedData = labelData
        .map((item: any) => ({
          text: item.labelName,
          value: item.labelName,
          deviceCount: item.deviceCount,
        }))
        .sort((a, b) => a.text.localeCompare(b.text));

      dispatch(setLabelList(sortedData));
      const selectedStillExists = selectedItem
        ? sortedData.some((label) => label.value === selectedItem.value)
        : false;

      const nextSelectedItem = selectedStillExists ? selectedItem : sortedData[0];

      if (!selectedItem || nextSelectedItem?.value !== selectedItem.value) {
        setSelectedItem(nextSelectedItem);
      }

      if (nextSelectedItem) {
        dispatch(setLabelName(nextSelectedItem));
      }
    } else {
      dispatch(setLabelList([]));
      setSelectedItem(undefined);
      dispatch(setLabelName(undefined));
    }
  }, [labelsData.data, dispatch, selectedItem]);

  const [deleteLabel, deleteLabelStatus] = useDeleteLabelMutation();

  const labelDeleteHandler = async (item: any) => {
    try {
      const response: any = await deleteLabel(item.value);
      if (response?.error || deleteLabelStatus.error) throw response.error;

      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "success",
          message: `Label: ${item.value} deleted successfully`,
        })
      );
      await labelsData.refetch();
    } catch (error: any) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };

  return {
    labelDeleteHandler,
    devicesComponent,
    labelList,
    selectedItem,
    setSelectedItem,
  };
};